/* eslint-disable */
/* eslint-disable eslint-comments/no-unused-disable */
/* 
* The file is automatically generated by Serenity UI.
* 
* ⚠️ WARNING: Any manual changes may be overwritten during the next upgrade
* 
* Found an issue? Submit a task to the ClickUp board:
* https://app.clickup.com/1255586/v/l/6-901510012553-1?pr=90154030902
*/
import { type DOMConversionMap, type DOMConversionOutput, type DOMExportOutput, DecoratorNode, type NodeKey, type SerializedLexicalNode, type Spread } from 'lexical';
import type { ReactNode } from 'react';

type RendererFn = (data: Record<string, unknown>) => ReactNode;
type GetTextContentFn = (data: Record<string, unknown>) => string;

interface FeatureEntry {
  renderer: RendererFn;
  getTextContent?: GetTextContentFn;
}

interface SerializedFeatureNode
  extends Spread<{ featureId: string; data: Record<string, unknown> }, SerializedLexicalNode> {
  type: 'feature-node';
}

/**
 * A single Lexical `DecoratorNode` class that backs every feature registered with `ChatTextField`.
 * Stores the originating `featureId` and the arbitrary `data` blob passed to `insert()`.
 *
 * Both the visual renderer and the plain-text representation are supplied by `ChatFeature` descriptors
 * and registered via `FeatureNode.setFeatures()`, which `ChatTextField` calls synchronously during render.
 */
class FeatureNode extends DecoratorNode<ReactNode> {
  __featureId: string;
  __data: Record<string, unknown>;

  static #features = new Map<string, FeatureEntry>();

  static setFeatures(entries: readonly ({ id: string } & FeatureEntry)[]): void {
    for (const { id, renderer, getTextContent } of entries) {
      FeatureNode.#features.set(id, { renderer, getTextContent });
    }
  }

  static getType(): string {
    return 'feature-node';
  }

  static clone(node: FeatureNode): FeatureNode {
    return new FeatureNode(node.__featureId, node.__data, node.__key);
  }

  static importJSON(serializedNode: SerializedFeatureNode): FeatureNode {
    return new FeatureNode(serializedNode.featureId, serializedNode.data);
  }

  static create(featureId: string, data: Record<string, unknown>): FeatureNode {
    return new FeatureNode(featureId, data);
  }

  constructor(featureId: string, data: Record<string, unknown>, key?: NodeKey) {
    super(key);
    this.__featureId = featureId;
    this.__data = data;
  }

  exportJSON(): SerializedFeatureNode {
    return {
      ...super.exportJSON(),
      data: this.__data,
      featureId: this.__featureId,
      type: 'feature-node',
    };
  }

  /**
   * Returns the plain-text content of this node.
   * Calls the `getTextContent` function registered for this feature id.
   * Falls back to the first string value found in `data` when none is registered.
   */
  getTextContent(): string {
    const entry = FeatureNode.#features.get(this.__featureId);
    if (entry?.getTextContent) return entry.getTextContent(this.__data);
    return Object.values(this.__data).find((v): v is string => typeof v === 'string') ?? '';
  }

  createDOM(): HTMLElement {
    const span = document.createElement('span');
    span.style.display = 'inline';
    return span;
  }

  updateDOM(): boolean {
    return false;
  }

  static importDOM(): DOMConversionMap | null {
    return {
      span: (node: Node) => {
        const el = node as HTMLElement;
        if (!el.hasAttribute?.('data-lexical-feature-node')) return null;
        return {
          conversion: (domNode: Node): DOMConversionOutput | null => {
            const el = domNode as HTMLElement;
            const featureId = el.getAttribute('data-lexical-feature-node');
            if (!featureId) return null;
            return { node: new FeatureNode(featureId, {}) };
          },
          priority: 1,
        };
      },
    };
  }

  exportDOM(): DOMExportOutput {
    const element = document.createElement('span');
    element.setAttribute('data-lexical-feature-node', this.__featureId);
    element.textContent = this.getTextContent();
    return { element };
  }

  decorate(): ReactNode {
    return FeatureNode.#features.get(this.__featureId)?.renderer(this.__data) ?? null;
  }
}

export { FeatureNode, type SerializedFeatureNode };
